<script setup lang="ts">
import { ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import { maskDateBR } from "@/common/utils/mask";

import type { MemberType } from "@/common/types/member";

import { baseInput } from "@/common/components";

const { t } = useI18n();

const emit = defineEmits(["update:data", "validate"]);

interface MemberRegisterType {
  name: string;
  login: string;
  email: string;
  birthdate: string;
  password: string;
}

const props = defineProps<{
  data: MemberType | null;
  isPatch?: boolean;
  isEdit?: boolean;
}>();

const DEFAULT_DATA = {
  name: "",
  login: "",
  email: "",
  birthdate: "",
  password: Math.random().toString(36).slice(-10),
};

const payload = ref<MemberRegisterType>({ ...DEFAULT_DATA });
const errors = ref<{ [key: string]: string }>({});

function validate() {
  const free = ["password"];

  Object.keys(payload.value).forEach((key) => {
    delete errors.value[key];

    if (!payload.value[key] && !free.includes(key))
      errors.value[key] = t("error.required");
  });

  emit("validate", Object.keys(errors.value).length === 0);
}

function update(key: keyof typeof payload.value) {
  validate();

  if (props.isPatch) {
    if (payload.value[key] === props.data[key]) return;

    emit("update:data", { [key]: payload.value[key] });
    return;
  }

  emit("update:data", payload.value);
}

function resumeData() {
  if (!props.data) return;

  const { first_name, last_name, email, login } = props.data;

  payload.value = {
    ...payload.value,
    name: `${first_name ?? ""} ${last_name ?? ""}`,
    email,
    login,
  };
}

watch(
  () => props.data,
  () => {
    resumeData();
  },
  { immediate: true }
);
</script>

<template>
	<article class="form-member">
		<base-input
			v-model="payload.name"
			:value="payload.name"
			:placeholder="t('common.placeholders.name')"
			required
			maxlength="75"
			@blur="update('name')"
		>
			<template #label> {{ t("common.labels.name") }} *</template>
			<template v-if="errors.name" #error> {{ errors.name }} </template>
		</base-input>

		<base-input
			v-model="payload.email"
			:value="payload.email"
			:placeholder="t('common.placeholders.email')"
			required
			maxlength="100"
			@blur="update('email')"
		>
			<template #label> {{ t("common.labels.email") }} *</template>
			<template v-if="errors.email" #error> {{ errors.email }} </template>
		</base-input>

		<section class="flex flex-row gap-4">
			<base-input
				v-model="payload.login"
				:value="payload.login"
				:placeholder="t('common.placeholders.login')"
				required
				maxlength="75"
				@blur="update('login')"
			>
				<template #label> {{ t("common.labels.login") }} *</template>
				<template v-if="errors.login" #error>{{ errors.login }}</template>
			</base-input>

			<base-input
				v-model="payload.birthdate"
				:value="payload.birthdate"
				:mask="maskDateBR"
				placeholder="dd/mm/yyyy"
				minLength="10"
				maxLength="10"
				@blur="update('birthdate')"
			>
				<template #label> {{ t("common.labels.birthdate") }} * </template>
				<template v-if="errors.birthdate" #error>
					{{ errors.birthdate }}
				</template>
			</base-input>
		</section>
	</article>
</template>

<style lang="scss" scoped>
.form-member {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 1rem;

  .group-name {
    display: grid;
    grid-template-columns: 1fr 8rem 8rem;
    gap: 1rem;
  }
}
</style>
